import { get } from 'aws-amplify/api';

export type FeatureFlagValue = string | boolean | Record<string, unknown> | null;

export type FeatureFlagRecord = {
  pk: string;
  sk: string;
  value: FeatureFlagValue;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
};

async function requestFeatureFlagsGet<T>(path: string): Promise<T> {
  const apiResponse = get({
    apiName: 'FeatureFlagsAPI',
    path,
  });
  const response = await apiResponse.response;
  const bodyText = await response.body.text();

  if (!bodyText) {
    throw new Error(`Empty feature flags response body for path: ${path}`);
  }

  return JSON.parse(bodyText) as T;
}

export async function listGlobalFlags(): Promise<FeatureFlagRecord[]> {
  return requestFeatureFlagsGet<FeatureFlagRecord[]>('/api/feature-flags/all/');
}

export async function listTenantFlags(tenantId: string): Promise<FeatureFlagRecord[]> {
  return requestFeatureFlagsGet<FeatureFlagRecord[]>(`/api/feature-flags/all/${tenantId}`);
}

export async function listUserFlags(
  tenantId: string,
  userId: string,
): Promise<FeatureFlagRecord[]> {
  return requestFeatureFlagsGet<FeatureFlagRecord[]>(
    `/api/feature-flags/all/${tenantId}/${userId}`,
  );
}

export async function getGlobalFlag(slug: string): Promise<FeatureFlagRecord> {
  return requestFeatureFlagsGet<FeatureFlagRecord>(`/api/feature-flags/${slug}`);
}

// Tenant flags change rarely but are read by several hooks and thunks on
// every Wealth visit, so one lookup per flag and firm is shared for a few
// minutes. Failed lookups are not kept, so the next caller retries.
const TENANT_FLAG_CACHE_MS = 5 * 60 * 1000;
const tenantFlagCache = new Map<string, { promise: Promise<FeatureFlagRecord>; at: number }>();

export function clearTenantFlagCache() {
  tenantFlagCache.clear();
}

export function getTenantFlag(slug: string, tenantId: string): Promise<FeatureFlagRecord> {
  const key = `${slug}/${tenantId}`;
  const cached = tenantFlagCache.get(key);
  if (cached && Date.now() - cached.at < TENANT_FLAG_CACHE_MS) {
    return cached.promise;
  }

  const promise = requestFeatureFlagsGet<FeatureFlagRecord>(`/api/feature-flags/${key}`);
  tenantFlagCache.set(key, { promise, at: Date.now() });
  promise.catch(() => {
    if (tenantFlagCache.get(key)?.promise === promise) {
      tenantFlagCache.delete(key);
    }
  });
  return promise;
}

export async function getUserFlag(
  slug: string,
  tenantId: string,
  userId: string,
): Promise<FeatureFlagRecord> {
  return requestFeatureFlagsGet<FeatureFlagRecord>(
    `/api/feature-flags/${slug}/${tenantId}/${userId}`,
  );
}
